import { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";
import {
  Autocomplete,
  GoogleMap,
  LoadScript,
  Marker,
} from "@react-google-maps/api";
import { serviceKeys } from "../../../constants/index";
import { createEventArtist, searchEventArtists } from "../../../api/artistApi";

const libraries = ["places"];

const splitDateTime = (dt) => {
  if (!dt) return ["", ""];
  const [d, t] = dt.split("T");
  return [d, t?.slice(0, 5)];
};

const TagInput = ({ label, values, onChange }) => {
  const [input, setInput] = useState("");

  const addItem = () => {
    if (!input.trim()) return;
    onChange([...values, input]);
    setInput("");
  };

  const removeItem = (index) => {
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <div>
      <label className="block text-sm text-slate-300 mb-1">{label}</label>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mb-2">
        {values?.map((item, i) => (
          <div
            key={i}
            className="flex items-center gap-2 bg-slate-800 border border-white/10 px-3 py-1 rounded-lg text-sm"
          >
            {item}
            <button
              onClick={() => removeItem(i)}
              className="text-slate-400 hover:text-red-400"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Add ${label}`}
          className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
        />
        <button
          onClick={addItem}
          className="px-3 py-2 bg-pink-600 hover:bg-pink-700 rounded text-sm"
        >
          Add
        </button>
      </div>
    </div>
  );
};

const EditMainInfoModal = ({ isOpen, onClose, onSubmit, loading, data }) => {
  const [form, setForm] = useState({});

  const [flyerHorizontalUpdated, setFlyerHorizontalUpdated] = useState(false);
  const [flyerPortraitUpdated, setFlyerPortraitUpdated] = useState(false);

  const [autocomplete, setAutocomplete] = useState(null);

  const [artistQuery, setArtistQuery] = useState("");
  const [artistResults, setArtistResults] = useState([]);
  const [selectedArtists, setSelectedArtists] = useState(
    data?.event_artists || [],
  );

  const [showCreateArtist, setShowCreateArtist] = useState(false);

  const [newArtist, setNewArtist] = useState({
    name: "",
    description: "",
    profile_image: null,
  });

  const center = {
    lat: Number(form.map_latitude) || 40.416775, // default madrid
    lng: Number(form.map_longitude) || -3.70379,
  };

  useEffect(() => {
    if (!artistQuery) {
      setArtistResults([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const data = await searchEventArtists(artistQuery);
        setArtistResults(data.results || []);
      } catch (err) {
        // console.log(err);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [artistQuery]);

  useEffect(() => {
    if (!isOpen) return;

    const [sd, st] = splitDateTime(data?.date_start);
    const [ed, et] = splitDateTime(data?.date_end);

    setForm({
      name: data?.name || "",
      //link: data?.pre_register_landing_page || "",
      distribution_channels: data?.distribution_channels || [],
      category: data?.category || "",
      date_start: sd,
      time_start: st,
      date_end: ed,
      time_end: et,
      client_commission_percentage: data?.client_commission_percentage || 0,
      commission_vat_included: data?.commission_vat_included || false,
      commission_vat_percentage: data?.commission_vat_percentage || 0,
      currency: data?.currency || "EUR",
      can_buy_without_login: data?.can_buy_without_login || false,
      meta_tags: data?.meta_tags || [],
      flyer_horizontal: data?.flyer_horizontal || null,
      flyer_portrait: data?.flyer_portrait || null,
      map_address: data?.map_address || "",
      map_latitude: data?.map_latitude || "",
      map_longitude: data?.map_longitude || "",
      // venue_name: data.venue?.name || "",
      // venue_addressLine1: data.venue?.address.address_1 || "",
      // venue_addressLine2: data.venue?.address.address_2 || "",
      // venue_addressLine3: data.venue?.address.address_3 || "",
      // venue_city: data.venue?.address.city || "",
      // venue_county: data.venue?.address.county || "",
      // venue_country: data.venue?.address.country || "",
      // venue_postcode: data.venue?.address.postcode || "",
      event_specific_type: data?.event_specific_type || "",
      dress_code: data?.dress_code || "",
    });
    setFlyerHorizontalUpdated(false);
    setFlyerPortraitUpdated(false);
    setShowCreateArtist(false);
    setNewArtist({
      name: "",
      description: "",
      profile_image: null,
    });

    setArtistQuery("");
  }, [isOpen, data]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    const start = `${form.date_start}T${form.time_start || "00:00"}:00`;
    const end = `${form.date_end}T${form.time_end || "00:00"}:00`;

    onSubmit({
      ...form,
      updated_flyer_horizontal: flyerHorizontalUpdated,
      updated_flyer_portrait: flyerPortraitUpdated,
      date_start: start,
      date_end: end,
      artists: selectedArtists.map((a) => a.id),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* HEADER */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">
            Edit Event Details
          </h2>

          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* BODY */}
        <div className="px-6 py-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Name */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Event Name
            </label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
            />
          </div>

          {/* Link */}
          {/* <div>
            <label className="block text-sm text-slate-300 mb-1">Link</label>
            <input
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
            />
          </div> */}

          {/* Arrays */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Channels
            </label>

            {/* Selected Tags */}
            <div className="flex flex-wrap gap-2 mb-2">
              {form.distribution_channels?.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 bg-slate-800 border border-white/10 px-3 py-1 rounded-lg text-sm"
                >
                  {item}
                  <button
                    onClick={() =>
                      setForm({
                        ...form,
                        distribution_channels:
                          form.distribution_channels.filter(
                            (_, idx) => idx !== i,
                          ),
                      })
                    }
                    className="text-slate-400 hover:text-red-400"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {/* Dropdown */}
            <select
              value=""
              onChange={(e) => {
                const value = e.target.value;
                if (!value) return;

                // prevent duplicates
                if (form.distribution_channels?.includes(value)) {
                  e.target.selectedIndex = 0; // reset dropdown
                  return;
                }

                setForm({
                  ...form,
                  distribution_channels: [...form.distribution_channels, value],
                });

                e.target.selectedIndex = 0; // reset dropdown after add
              }}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
            >
              <option value="">Select channel</option>
              <option value="mobile">Mobile</option>
              <option value="website">Website</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Category:
            </label>
            <select
              value={form.category || ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  category: e.target.value,
                })
              }
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
            >
              <option value="">Select category</option>
              <option value="art">Art</option>
              <option value="charity">Charity</option>
              <option value="contest">Contest</option>
              <option value="cultural">Cultural</option>
              <option value="digital">Digital</option>
              <option value="festival">Festival</option>
              <option value="education">Education</option>
              <option value="movie">Movie</option>
              <option value="food_event">Food event</option>
              <option value="kids">Kids</option>
              <option value="open_bar">Open Bar</option>
              <option value="party">Party</option>
              <option value="pets">Pets</option>
              <option value="pool_party">Pool Party</option>
              <option value="stand_up">Stand-up</option>
              <option value="theater">Theater</option>
              <option value="spa_wellness">Spa/ Wellness</option>
            </select>
          </div>

          <TagInput
            label="Meta Tags"
            values={form.meta_tags || []}
            onChange={(val) =>
              setForm({
                ...form,
                meta_tags: val,
              })
            }
          />

          <div>
            <p className="text-sm text-slate-300 mb-2">Flyers</p>

            <div className="grid grid-cols-2 gap-4">
              {/* Horizontal */}
              <div>
                <p className="text-xs text-slate-400 mb-1">Horizontal</p>
                <p className="text-xs text-slate-400 mb-1">
                  Only JPEG, JPG, PNG accepted. <br /> Must be 1920x1080
                </p>

                {form.flyer_horizontal ? (
                  <div className="mt-3">
                    <img
                      src={
                        typeof form.flyer_horizontal === "string"
                          ? form.flyer_horizontal
                          : URL.createObjectURL(form.flyer_horizontal)
                      }
                      alt="Flyer preview"
                      className="w-57 h-32 object-contain rounded-lg border border-white/10"
                    />
                  </div>
                ) : (
                  <div className="w-57 h-32 flex items-center justify-center bg-slate-800 rounded-lg text-slate-500 text-xs mb-2">
                    No Image
                  </div>
                )}

                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    const reset = () => (e.target.value = null);

                    if (
                      !["image/jpeg", "image/jpg", "image/png"].includes(
                        file.type,
                      )
                    ) {
                      reset();
                      return;
                    }

                    const img = new Image();
                    const url = URL.createObjectURL(file);

                    img.onload = () => {
                      if (img.width !== 1920 || img.height !== 1080) {
                        reset();
                        return;
                      }
                      setFlyerHorizontalUpdated(true);
                      setForm({ ...form, flyer_horizontal: file });
                      URL.revokeObjectURL(url);
                    };

                    img.onerror = () => {
                      reset();
                      URL.revokeObjectURL(url);
                    };

                    img.src = url;
                  }}
                  className="w-full text-xs text-slate-400"
                />
              </div>

              {/* Portrait */}
              <div>
                <p className="text-xs text-slate-400 mb-1">Portrait</p>
                <p className="text-xs text-slate-400 mb-1">
                  Only JPEG, JPG, PNG accepted. <br /> Must be 1080x1920
                </p>

                {form.flyer_portrait ? (
                  <div className="mt-3">
                    <img
                      src={
                        typeof form.flyer_portrait === "string"
                          ? form.flyer_portrait
                          : URL.createObjectURL(form.flyer_portrait)
                      }
                      alt="Flyer preview"
                      className="w-20 h-32 object-contain rounded-lg border border-white/10"
                    />
                  </div>
                ) : (
                  <div className="w-20 h-32 flex items-center justify-center bg-slate-800 rounded-lg text-slate-500 text-xs mb-2">
                    No Image
                  </div>
                )}

                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    const reset = () => (e.target.value = null);

                    if (
                      !["image/jpeg", "image/jpg", "image/png"].includes(
                        file.type,
                      )
                    ) {
                      reset();
                      return;
                    }

                    const img = new Image();
                    const url = URL.createObjectURL(file);

                    img.onload = () => {
                      if (img.width !== 1080 || img.height !== 1920) {
                        reset();
                        return;
                      }

                      setFlyerPortraitUpdated(true);
                      setForm({ ...form, flyer_portrait: file });
                      URL.revokeObjectURL(url);
                    };

                    img.onerror = () => {
                      reset();
                      URL.revokeObjectURL(url);
                    };

                    img.src = url;
                  }}
                  className="w-full text-xs text-slate-400"
                />
              </div>
            </div>
          </div>

          {/* Venue */}
          {/* <div>
            <h3 className="text-sm font-semibold text-white mb-3 mt-4">
              Venue Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              <input
                placeholder="Venue Name"
                value={form.venue_name}
                onChange={(e) => setForm({ ...form, venue_name: e.target.value })}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Address Line 1"
                value={form.venue_addressLine1}
                onChange={(e) =>
                  setForm({ ...form, venue_addressLine1: e.target.value })
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Address Line 2"
                value={form.venue_addressLine2}
                onChange={(e) =>
                  setForm({ ...form, venue_addressLine2: e.target.value })
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Address Line 3"
                value={form.venue_addressLine3}
                onChange={(e) =>
                  setForm({ ...form, venue_addressLine3: e.target.value })
                }
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="City"
                value={form.venue_city}
                onChange={(e) => setForm({ ...form, venue_city: e.target.value })}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="County"
                value={form.venue_county}
                onChange={(e) => setForm({ ...form, venue_county: e.target.value })}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Country"
                value={form.venue_country}
                onChange={(e) => setForm({ ...form, venue_country: e.target.value })}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />

              <input
                placeholder="Postcode"
                value={form.venue_postcode}
                onChange={(e) => setForm({ ...form, venue_postcode: e.target.value })}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div> */}

          <LoadScript
            googleMapsApiKey={serviceKeys.API_KEY}
            libraries={libraries}
          >
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Venue Address
              </label>
              <p className="block text-xs text-slate-300 mb-1">
                {form.map_address}
              </p>
              <Autocomplete
                onLoad={(auto) => setAutocomplete(auto)}
                onPlaceChanged={() => {
                  if (autocomplete) {
                    const place = autocomplete.getPlace();

                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    const address = place.formatted_address;

                    setForm({
                      ...form,
                      map_address: address,
                      map_latitude: lat,
                      map_longitude: lng,
                    });
                  }
                }}
              >
                <input
                  type="text"
                  placeholder="Search location"
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 mb-2 text-sm text-white"
                />
              </Autocomplete>

              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "300px" }}
                center={center}
                zoom={12}
                onClick={(e) => {
                  const lat = e.latLng.lat();
                  const lng = e.latLng.lng();

                  setForm({ ...form, map_latitude: lat, map_longitude: lng });
                }}
              >
                {form.map_latitude && form.map_longitude && (
                  <Marker
                    position={{
                      lat: Number(form.map_latitude),
                      lng: Number(form.map_longitude),
                    }}
                    draggable
                    onDragEnd={(e) => {
                      const lat = e.latLng.lat();
                      const lng = e.latLng.lng();

                      setForm({
                        ...form,
                        map_latitude: lat,
                        map_longitude: lng,
                      });
                    }}
                  />
                )}
              </GoogleMap>
            </div>
          </LoadScript>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4 mt-16">
            {/* Start */}
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                Start Date & Time
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={form.date_start}
                  onChange={(e) =>
                    setForm({ ...form, date_start: e.target.value })
                  }
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
                />
                <input
                  type="time"
                  value={form.time_start}
                  onChange={(e) =>
                    setForm({ ...form, time_start: e.target.value })
                  }
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
                />
              </div>
            </div>

            {/* End */}
            <div>
              <label className="block text-sm text-slate-300 mb-1">
                End Date & Time
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={form.date_end}
                  onChange={(e) =>
                    setForm({ ...form, date_end: e.target.value })
                  }
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
                />
                <input
                  type="time"
                  value={form.time_end}
                  onChange={(e) =>
                    setForm({ ...form, time_end: e.target.value })
                  }
                  className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
                />
              </div>
            </div>
          </div>

          {/* Commission */}
          <div>
            <label className="text-sm text-slate-300 mb-1">
              Client Commission (%)
            </label>
            <input
              type="number"
              value={form.client_commission_percentage}
              onChange={(e) =>
                setForm({
                  ...form,
                  client_commission_percentage: e.target.value,
                })
              }
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
            />
          </div>

          <div>
            <label className="text-sm text-slate-300 mb-1">
              VAT Included in Commission
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.commission_vat_included === true}
                  onChange={() =>
                    setForm({ ...form, commission_vat_included: true })
                  }
                />
                Yes
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.commission_vat_included === false}
                  onChange={() =>
                    setForm({ ...form, commission_vat_included: false })
                  }
                />
                No
              </label>
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-300 mb-1">VAT (%)</label>
            <input
              type="number"
              value={form.commission_vat_percentage}
              onChange={(e) =>
                setForm({
                  ...form,
                  commission_vat_percentage: e.target.value,
                })
              }
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
            />
          </div>

          <div>
            <h3 className="text-sm text-slate-300 mb-1">Select Currency</h3>
            <select
              value={form.currency}
              onChange={(e) => 
                setForm({
                  ...form,
                  currency: e.target.value
                })}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="RON">RON</option>
              <option value="AED">AED</option>
            </select>
          </div>

          {/* Event Type */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="text-sm text-slate-300">
                Event Specific Type
              </label>
              <div className="relative group cursor-pointer">
                <span className="text-xs text-pink-700 border border-pink-700 rounded-full w-4 h-4 flex items-center justify-center">
                  i
                </span>
                <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 bg-slate-800 text-xs text-white p-2 rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition z-50">
                  This information will be visible on your event page.
                </div>
              </div>
            </div>
            <input
              value={form.event_specific_type}
              onChange={(e) =>
                setForm({ ...form, event_specific_type: e.target.value })
              }
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Dress Code
            </label>
            <input
              value={form.dress_code}
              onChange={(e) => setForm({ ...form, dress_code: e.target.value })}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
            />
          </div>

          <div>
            <p className="text-sm text-slate-300 mb-2">Artist Lineup</p>

            {/* SEARCH INPUT */}
            <input
              value={artistQuery}
              onChange={(e) => setArtistQuery(e.target.value)}
              placeholder="Search artist to add..."
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2 text-sm text-white"
            />

            {/* SEARCH RESULTS */}
            {artistResults.length > 0 && (
              <div className="mt-2 bg-slate-800 border border-white/10 rounded-lg max-h-40 overflow-y-auto">
                {artistResults.map((artist) => (
                  <div
                    key={artist.id}
                    onClick={() => {
                      if (selectedArtists.find((a) => a.id === artist.id))
                        return;

                      setSelectedArtists([...selectedArtists, artist]);
                      setArtistQuery("");
                      setArtistResults([]);
                    }}
                    className="px-3 py-2 hover:bg-slate-700 cursor-pointer flex items-center gap-3"
                  >
                    <img
                      src={artist.profile_image}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-white text-sm">{artist.name}</p>
                      <p className="text-xs text-slate-400">
                        {artist.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* NO RESULTS → CREATE */}
            {artistQuery && artistResults.length === 0 && !showCreateArtist && (
              <button
                onClick={() => {
                  setShowCreateArtist(true);
                  setNewArtist((prev) => ({
                    ...prev,
                    name: artistQuery,
                  }));
                }}
                className="mt-2 text-sm text-pink-500 hover:underline"
              >
                + Create "{artistQuery}"
              </button>
            )}

            {/* SELECTED ARTISTS */}
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedArtists.map((artist) => (
                <div
                  key={artist.id}
                  className="flex items-center gap-2 bg-slate-800 border border-white/10 px-3 py-1 rounded-lg"
                >
                  <img
                    src={artist.profile_image}
                    className="w-6 h-6 rounded-full object-cover"
                  />

                  <span className="text-sm text-white">{artist.name}</span>

                  <button
                    onClick={() =>
                      setSelectedArtists(
                        selectedArtists.filter((a) => a.id !== artist.id),
                      )
                    }
                    className="text-red-400 hover:text-red-600"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          {showCreateArtist && (
            <div className="mt-4 p-4 border border-white/10 rounded-lg bg-[#0f172a] space-y-3">
              <p className="text-sm text-white font-medium">Create Artist</p>

              {/* NAME */}
              <input
                value={newArtist.name}
                onChange={(e) =>
                  setNewArtist({ ...newArtist, name: e.target.value })
                }
                placeholder="Artist name"
                className="w-full bg-[#131C31] border border-white/10 rounded px-3 py-2 text-sm text-white"
              />

              {/* DESCRIPTION */}
              <input
                value={newArtist.description}
                onChange={(e) =>
                  setNewArtist({ ...newArtist, description: e.target.value })
                }
                placeholder="Short description"
                className="w-full bg-[#131C31] border border-white/10 rounded px-3 py-2 text-sm text-white"
              />

              {/* IMAGE */}
              <div>
                <p className="text-xs text-slate-400 mb-1">Profile Image</p>

                {newArtist.profile_image && (
                  <img
                    src={URL.createObjectURL(newArtist.profile_image)}
                    className="w-16 h-16 rounded object-cover mb-2"
                  />
                )}

                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    setNewArtist({ ...newArtist, profile_image: file });
                  }}
                  className="text-xs text-slate-400"
                />
              </div>

              {/* ACTIONS */}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowCreateArtist(false)}
                  className="px-3 py-1 text-sm text-slate-300 hover:bg-slate-800 rounded"
                >
                  Cancel
                </button>

                <button
                  onClick={async () => {
                    try {
                      const formData = new FormData();
                      formData.append("name", newArtist.name);
                      formData.append("description", newArtist.description);

                      if (newArtist.profile_image) {
                        formData.append(
                          "profile_image",
                          newArtist.profile_image,
                        );
                      }

                      const created = await createEventArtist(formData);

                      setSelectedArtists([...selectedArtists, created]);

                      setShowCreateArtist(false);
                      setNewArtist({
                        name: "",
                        description: "",
                        profile_image: null,
                      });

                      setArtistQuery("");
                    } catch (err) {
                      //console.log(err);
                    }
                  }}
                  className="px-3 py-1 text-sm bg-pink-600 hover:bg-pink-700 rounded text-white"
                >
                  Create & Add
                </button>
              </div>
            </div>
          )}

          {/* Boolean */}
          <div>
            <label className="text-sm text-slate-300 mb-1">
              Can buy without login
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.can_buy_without_login === true}
                  onChange={() =>
                    setForm({ ...form, can_buy_without_login: true })
                  }
                />
                Yes
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  className="accent-pink-600"
                  checked={form.can_buy_without_login === false}
                  onChange={() =>
                    setForm({ ...form, can_buy_without_login: false })
                  }
                />
                No
              </label>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700"
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditMainInfoModal;
